import axios from "axios";
import accessToken from "./jwt-token-access/accessToken";

const buildQueryParams = (params) => {
  const ignoreParams = [
    "totalDocs",
    "totalPages",
    "rangeStartStepCompleted",
    "rangeEndStepCompleted",
    "dateRangeSelectionStatus",
  ];
  const tempArr = [];
  for (const key in params) {
    if (ignoreParams.includes(key)) {
      continue;
    }
    if (Object.hasOwnProperty.call(params, key)) {
      const element = params[key];
      if (element !== "" && element !== null && element !== undefined) {
        tempArr.push(key + "=" + element);
      }
    }
  }
  return tempArr.join("&");
};

// export const API_URL = "http://localhost:8080/v1";
export const API_URL = "https://backend.bharatcredits.com/v1";
const REFRESH_ENDPOINT = "/auth/refresh-tokens";
const LOGOUT_ENDPOINT = "/auth/logout";

const getStoredRefreshToken = () => localStorage.getItem("refreshToken") || "";
const getStoredAccessExpiry = () => localStorage.getItem("accessTokenExpires");

const saveAuthTokens = (tokens = {}) => {
  if (tokens.access?.token) {
    localStorage.setItem("accessToken", tokens.access.token);
  }
  if (tokens.access?.expires) {
    localStorage.setItem("accessTokenExpires", tokens.access.expires);
  }
  if (tokens.refresh?.token) {
    localStorage.setItem("refreshToken", tokens.refresh.token);
  }
  if (tokens.refresh?.expires) {
    localStorage.setItem("refreshTokenExpires", tokens.refresh.expires);
  }
};

const extractTokensFromResponse = (responseData = {}) => {
  if (responseData?.tokens) {
    return responseData.tokens;
  }
  // Fallback for APIs that return { access, refresh } directly
  return {
    access: responseData?.access,
    refresh: responseData?.refresh,
  };
};

const refreshAccessToken = async () => {
  const latestRefreshToken = getStoredRefreshToken();
  if (!latestRefreshToken) {
    throw new Error("Missing refresh token");
  }

  const response = await axiosApi.post(
    REFRESH_ENDPOINT,
    { refreshToken: latestRefreshToken },
    { skipRefresh: true }
  );
  const tokens = extractTokensFromResponse(response.data);
  saveAuthTokens(tokens);
  return response.data;
};

const clearAuthState = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("accessTokenExpires");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("refreshTokenExpires");
  localStorage.removeItem("user");
};

const axiosApi = axios.create({
  baseURL: API_URL,
});
let refreshPromise = null;

axiosApi.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    const token = accessToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const shouldAttemptRefresh = (error) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message || "").toLowerCase();
  return (
    status === 401 ||
    (status === 403 &&
      (message.includes("please authenticate") ||
        message.includes("authenticate") ||
        message.includes("token")))
  );
};

const isTokenRoute = (requestUrl = "") => {
  return (
    requestUrl.includes(REFRESH_ENDPOINT) || requestUrl.includes(LOGOUT_ENDPOINT)
  );
};

const maybeRefreshEarly = async () => {
  const accessExpiresAt = getStoredAccessExpiry();
  const refreshToken = getStoredRefreshToken();
  if (!accessExpiresAt || !refreshToken) {
    return;
  }

  const expiresMs = new Date(accessExpiresAt).getTime();
  const nowMs = Date.now();
  const oneMinuteMs = 60 * 1000;
  if (Number.isNaN(expiresMs) || expiresMs - nowMs > oneMinuteMs) {
    return;
  }

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken()
      .catch((error) => {
        clearAuthState();
        window.location.href = "/login";
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  await refreshPromise;
};

axiosApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const alreadyRetried = originalRequest._retry;
    const skipRefresh = originalRequest.skipRefresh;
    const requestUrl = originalRequest.url || "";

    if (
      skipRefresh ||
      alreadyRetried ||
      !shouldAttemptRefresh(error) ||
      isTokenRoute(requestUrl)
    ) {
      return Promise.reject(error);
    }

    if (!getStoredRefreshToken()) {
      clearAuthState();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken()
          .finally(() => {
            refreshPromise = null;
          });
      }

      await refreshPromise;
      const nextAccessToken = accessToken();
      if (nextAccessToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = nextAccessToken;
      }
      return axiosApi(originalRequest);
    } catch (refreshError) {
      clearAuthState();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    }
  }
);

export async function get(url, params = {}, config = {}) {
  await maybeRefreshEarly();
  const queryString = buildQueryParams(params);
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  return await axiosApi
    .get(fullUrl, { ...config })
    .then((response) => response.data);
}

export async function post(url, data, config = {}) {
  if (url !== REFRESH_ENDPOINT) {
    await maybeRefreshEarly();
  }
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function postFormData(url, formData, config = {}) {
  await maybeRefreshEarly();
  return axiosApi
    .post(url, formData, {
      ...config,
      headers: { ...config.headers, "Content-Type": "multipart/form-data" },
    })
    .then((response) => response.data);
}

export async function put(url, data, config = {}) {
  await maybeRefreshEarly();
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url, config = {}) {
  await maybeRefreshEarly();
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}
export const getLoggedinUser = () => {
  return JSON.parse(localStorage.getItem("user"));
};
